import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Avatar,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Pets as PetIcon,
  HealthAndSafety as HealthIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import {
  RecentMemoryContext,
  MemoryAnalytics,
  MEMORY_TEXTS
} from '../types/recentMemory';
import { recentConversationMemoryService } from '../services/recentConversationMemoryService';

interface RecentConversationMemoryProps {
  userId: string;
  petName: string;
  petType: 'dog' | 'cat' | 'both';
  language: 'zh-CN' | 'es' | 'fr' | 'en';
  onMemoryContextUpdate?: (context: RecentMemoryContext) => void;
}

const RecentConversationMemory: React.FC<RecentConversationMemoryProps> = ({
  userId,
  petName,
  petType,
  language,
  onMemoryContextUpdate
}) => {
  const [memoryContext, setMemoryContext] = useState<RecentMemoryContext | null>(null);
  const [analytics, setAnalytics] = useState<MemoryAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    recentConversations: true,
    petProfile: false,
    analytics: false
  });

  // Get language-specific texts
  const texts = MEMORY_TEXTS[language] || MEMORY_TEXTS['en'];

  const loadMemoryData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🧠 Loading memory data for pet:', petName);

      // Load memory context
      const contextResult = await recentConversationMemoryService.getPetMemoryContext(userId, petName);
      if (contextResult.success && contextResult.context) {
        setMemoryContext(contextResult.context);
        onMemoryContextUpdate?.(contextResult.context);
      } else {
        setError(contextResult.error || texts.memoryRetrievalFailed);
        return;
      }

      // Load analytics
      const analyticsData = await recentConversationMemoryService.getMemoryAnalytics(userId, petName);
      setAnalytics(analyticsData);

      console.log('✅ Memory data loaded successfully');

    } catch (error) {
      console.error('❌ Failed to load memory data:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [userId, petName, petType, language, onMemoryContextUpdate, texts.memoryRetrievalFailed]);

  useEffect(() => {
    loadMemoryData();
  }, [loadMemoryData]);

  const handleRefresh = () => {
    loadMemoryData();
  };

  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString(language === 'zh-CN' ? 'zh-CN' :
      language === 'es' ? 'es-ES' :
        language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const getPetTypeIcon = (type: string) => {
    switch (type) {
      case 'dog': return '🐕';
      case 'cat': return '🐱';
      default: return '🐾';
    }
  };

  const getTopicColor = (topic: string) => {
    const colors = ['primary', 'secondary', 'success', 'warning', 'info', 'error'];
    const hash = topic.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    return colors[hash % colors.length] as any;
  };

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={3}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {texts.loading || 'Loading memory data...'}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton color="inherit" size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!memoryContext) {
    return (
      <Alert severity="info">
        {texts.noRecentConversations}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <MemoryIcon color="primary" />
          <Typography variant="h6" component="h2">
            {texts.recentConversations} - {getPetTypeIcon(petType)} {petName}
          </Typography>
        </Box>
        <Tooltip title={texts.refresh || 'Refresh'}>
          <IconButton onClick={handleRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Recent Conversations Summary */}
      <Accordion
        expanded={expandedSections.recentConversations}
        onChange={() => handleSectionToggle('recentConversations')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <ChatIcon color="primary" />
            <Typography variant="subtitle1">
              {texts.recentConversations} ({memoryContext.totalConversations})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {memoryContext.recentSummaries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {texts.noRecentConversations}
              </Typography>
            ) : (
              <List dense>
                {memoryContext.recentSummaries.slice(0, 5).map((summary, index) => (
                  <React.Fragment key={summary.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          {index + 1}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(summary.lastUpdated)}
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 0.5 }}>
                              {summary.summary}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            {summary.keyTopics && summary.keyTopics.length > 0 && (
                              <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                                {summary.keyTopics.slice(0, 3).map((topic) => (
                                  <Chip
                                    key={topic}
                                    label={topic}
                                    size="small"
                                    color={getTopicColor(topic)}
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < memoryContext.recentSummaries.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Pet Profile */}
      <Accordion
        expanded={expandedSections.petProfile}
        onChange={() => handleSectionToggle('petProfile')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <PetIcon color="primary" />
            <Typography variant="subtitle1">
              {texts.petProfile}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
            {/* Personality */}
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                {texts.personality || 'Personality'}
              </Typography>
              {memoryContext.overallPetProfile.personality.length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {memoryContext.overallPetProfile.personality.map((trait, index) => (
                    <Chip key={index} label={trait} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No personality traits recorded yet
                </Typography>
              )}
            </Paper>

            {/* Preferences */}
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                {texts.preferences || 'Preferences'}
              </Typography>
              {memoryContext.overallPetProfile.preferences.length > 0 ? (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {memoryContext.overallPetProfile.preferences.map((pref, index) => (
                    <Chip key={index} label={pref} size="small" color="secondary" variant="outlined" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No preferences recorded yet
                </Typography>
              )}
            </Paper>

            {/* Achievements */}
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                {texts.achievements || 'Achievements'}
              </Typography>
              {memoryContext.overallPetProfile.achievements.length > 0 ? (
                <List dense>
                  {memoryContext.overallPetProfile.achievements.map((achievement, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <TrendingUpIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={achievement} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No achievements recorded yet
                </Typography>
              )}
            </Paper>

            {/* Health History */}
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                {texts.healthHistory || 'Health History'}
              </Typography>
              {memoryContext.overallPetProfile.healthHistory.length > 0 ? (
                <List dense>
                  {memoryContext.overallPetProfile.healthHistory.map((health, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon>
                        <HealthIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={health} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No health notes recorded yet
                </Typography>
              )}
            </Paper>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Analytics */}
      {analytics && (
        <Accordion
          expanded={expandedSections.analytics}
          onChange={() => handleSectionToggle('analytics')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUpIcon color="primary" />
              <Typography variant="subtitle1">
                {texts.analytics || 'Analytics'}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              {/* Most Discussed Topics */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {texts.mostDiscussedTopics || 'Most Discussed Topics'}
                </Typography>
                {analytics.mostDiscussedTopics.length > 0 ? (
                  <List dense>
                    {analytics.mostDiscussedTopics.slice(0, 5).map((topic, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={topic.topic}
                          secondary={`${topic.frequency} discussions`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No topic data available
                  </Typography>
                )}
              </Paper>

              {/* Conversation Patterns */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {texts.conversationPatterns || 'Conversation Patterns'}
                </Typography>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Average conversation length: {analytics.conversationPatterns.averageConversationLength} messages
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Most active time: {analytics.conversationPatterns.mostActiveTimeOfDay}
                  </Typography>
                  {analytics.conversationPatterns.preferredTopics.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Preferred topics:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {analytics.conversationPatterns.preferredTopics.map((topic, index) => (
                          <Chip key={index} label={topic} size="small" color="info" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Last Conversation Info */}
      <Box mt={2}>
        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ScheduleIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2">
              {texts.lastConversation}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {formatDate(memoryContext.lastConversationDate)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {texts.totalConversations}: {memoryContext.totalConversations}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default RecentConversationMemory;
